import { useCallback, useEffect, useRef } from "react";

import { CliCommandResponse } from "src/gen/schema/bilge/api";

const SCROLL_BOTTOM_THRESHOLD = 50;
/**
 * Keeps the mock shell output container pinned to the latest command output
 * while still allowing the user to manually scroll up and inspect older output.
 *
 * Behavior:
 * - When the user is already near the bottom, new command output automatically
 *   scrolls the container to the bottom.
 * - When the user manually scrolls up more than SCROLL_BOTTOM_THRESHOLD pixels,
 *   auto-scroll is paused so the view does not keep jumping back to the latest line.
 *   specifically during streaming.
 * - When a new streaming command starts, the manual-scroll state is reset so the
 *   new live command follows the latest output by default.
 * - When streaming stops or a non-streaming command result is rendered, the hook
 *   also resets the manual-scroll state so static command output can be brought
 *   into view.
 */

export const useAutoScroll = (
  isStreamingActive: boolean,
  cmdResult: CliCommandResponse | undefined,
  streamingOutput: string
) => {
  const outputContainerRef = useRef<HTMLDivElement>(null); // Ref for the scrollable output container.
  const isUserScrolledUpRef = useRef(false); // Tracks whether user manually scrolled away from bottom.
  const isAutoScrollingRef = useRef(false); // Tracks whether scroll was triggered by code, not user.
  const wasStreamingActiveRef = useRef(false); // Remembers previous streaming state to detect new streaming start.

  const scrollToBottom = useCallback((behavior: ScrollBehavior) => {
    const container = outputContainerRef.current;
    if (!container) return;

    isAutoScrollingRef.current = true; // Mark that the next scroll is caused by our code.

    container.scrollTo({
      top: container.scrollHeight, // Scroll to the very bottom.
      behavior // Use "smooth" or "auto" based on caller.
    });

    requestAnimationFrame(() => {
      // After browser gets a frame, allow scroll events to be treated as user scroll again.
      isAutoScrollingRef.current = false;
    });
  }, []);

  const handleOutputScroll = useCallback(() => {
    const container = outputContainerRef.current;
    // Ignore if missing, or if scroll came from our own code.
    if (!container || isAutoScrollingRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    // True when user is not close to bottom.
    isUserScrolledUpRef.current =
      scrollHeight - scrollTop - clientHeight > SCROLL_BOTTOM_THRESHOLD;
  }, []);

  useEffect(() => {
    if (!outputContainerRef.current) return;

    // True only on transition from not-streaming to streaming.
    const streamingJustStarted =
      isStreamingActive && !wasStreamingActiveRef.current;

    // Reset manual scroll state for new stream or non-streaming output.
    if (streamingJustStarted || !isStreamingActive) {
      isUserScrolledUpRef.current = false;
    }
    // Save current streaming state for next render.
    wasStreamingActiveRef.current = isStreamingActive;

    // Scroll if user has not manually scrolled up.
    if (!isUserScrolledUpRef.current) {
      scrollToBottom(isStreamingActive ? "auto" : "smooth");
    }
  }, [cmdResult, isStreamingActive, streamingOutput, scrollToBottom]);

  return { outputContainerRef, handleOutputScroll };
};
